import axiosInstance from '../../../api/axios';

/**
 * Knowledge Base API client.
 * Strictly adheres to workspace-level multi-tenancy invariants.
 */
const withWorkspace = (workspaceId) =>
  workspaceId ? { headers: { 'x-workspace-id': workspaceId } } : {};

const knowledgeApi = {
  /**
   * List knowledge sources for the active workspace
   */
  async getSources(workspaceId, params = {}) {
    const config = {
      ...withWorkspace(workspaceId),
      params: {
        category: params.category || undefined,
        status: params.status || undefined,
        approvalStatus: params.approvalStatus || undefined,
        freshnessStatus: params.freshnessStatus || undefined,
        knowledgeType: params.knowledgeType || undefined,
        search: params.search || undefined,
        limit: params.limit || undefined,
        offset: params.offset || undefined,
      },
    };
    const res = await axiosInstance.get('/knowledge/sources', config);
    return res.data?.data || [];
  },

  /**
   * Retrieve aggregated stats for the workspace knowledge base
   */
  async getStats(workspaceId) {
    const res = await axiosInstance.get('/knowledge/stats', withWorkspace(workspaceId));
    return (
      res.data?.data || {
        totalSources: 0,
        totalChunks: 0,
        totalTokens: 0,
        indexedCount: 0,
        processingCount: 0,
        reviewCount: 0,
        freshnessCounts: {
          current: 0,
          reviewDue: 0,
          expired: 0,
          superseded: 0,
          pendingVerification: 0,
        },
        categoryCounts: {},
      }
    );
  },

  /**
   * Retrieve a single knowledge source and its chunks
   */
  async getSource(workspaceId, id) {
    const res = await axiosInstance.get(`/knowledge/sources/${id}`, withWorkspace(workspaceId));
    return res.data?.data;
  },

  /**
   * Create a new text-based knowledge source with automatic RAG chunking
   */
  async createSource(workspaceId, data) {
    const res = await axiosInstance.post('/knowledge/sources', data, withWorkspace(workspaceId));
    return res.data?.data;
  },

  /**
   * Upload a document file (PDF, DOCX, TXT) for ingestion and indexing
   */
  async uploadSource(workspaceId, formData) {
    const config = {
      ...withWorkspace(workspaceId),
      headers: {
        ...withWorkspace(workspaceId).headers,
        'Content-Type': 'multipart/form-data',
      },
    };
    const res = await axiosInstance.post('/knowledge/sources/upload', formData, config);
    return res.data?.data;
  },

  /**
   * Approve a pending knowledge source (Owner, Admin, Manager only)
   */
  async approveSource(workspaceId, id) {
    const res = await axiosInstance.post(
      `/knowledge/sources/${id}/approve`,
      {},
      withWorkspace(workspaceId)
    );
    return res.data;
  },

  /**
   * Verify or renew a knowledge source (Review Due / Expired -> Current)
   */
  async verifySource(workspaceId, id, payload = {}) {
    const res = await axiosInstance.post(
      `/knowledge/sources/${id}/verify`,
      payload,
      withWorkspace(workspaceId)
    );
    return res.data;
  },

  /**
   * Mark a knowledge source as superseded by a replacement record
   */
  async supersedeSource(workspaceId, id, replacementSourceId) {
    const res = await axiosInstance.post(
      `/knowledge/sources/${id}/supersede`,
      { replacementSourceId },
      withWorkspace(workspaceId)
    );
    return res.data;
  },

  /**
   * Freshness policies & background sweep
   */
  async getFreshnessPolicies(workspaceId) {
    const res = await axiosInstance.get(
      '/knowledge/freshness/policies',
      withWorkspace(workspaceId)
    );
    return res.data?.data || [];
  },

  async upsertFreshnessPolicy(workspaceId, payload) {
    const res = await axiosInstance.put(
      '/knowledge/freshness/policies',
      payload,
      withWorkspace(workspaceId)
    );
    return res.data?.data;
  },

  async runFreshnessSweep(workspaceId) {
    const res = await axiosInstance.post(
      '/knowledge/freshness/sweep',
      {},
      withWorkspace(workspaceId)
    );
    return res.data?.data;
  },

  /**
   * Reject a pending knowledge source with a specified reason
   */
  async rejectSource(workspaceId, id, reason) {
    const res = await axiosInstance.post(
      `/knowledge/sources/${id}/reject`,
      { reason },
      withWorkspace(workspaceId)
    );
    return res.data;
  },

  /**
   * Delete a knowledge source
   */
  /**
   * Update visibility / knowledge type of a knowledge source ('internal' | 'external')
   */
  async updateSourceVisibility(workspaceId, id, knowledgeType) {
    const res = await axiosInstance.patch(
      `/knowledge/sources/${id}/visibility`,
      { knowledgeType },
      withWorkspace(workspaceId)
    );
    return res.data;
  },

  async deleteSource(workspaceId, id) {
    const res = await axiosInstance.delete(`/knowledge/sources/${id}`, withWorkspace(workspaceId));
    return res.data;
  },

  /**
   * Synchronize vectors and re-index workspace knowledge
   */
  async syncVectors(workspaceId) {
    const res = await axiosInstance.post('/knowledge/sync', {}, withWorkspace(workspaceId));
    return res.data?.data;
  },

  /**
   * Query raw knowledge chunks
   */
  async queryKnowledge(workspaceId, queryPayload) {
    const res = await axiosInstance.post(
      '/knowledge/query',
      queryPayload,
      withWorkspace(workspaceId)
    );
    return res.data?.data || [];
  },

  /**
   * Ask a question using grounded RAG semantic retrieval & answer generation
   */
  async askQuestion(workspaceId, queryPayload) {
    const res = await axiosInstance.post(
      '/knowledge/rag/query',
      queryPayload,
      withWorkspace(workspaceId)
    );
    return res.data?.data;
  },

  /**
   * Submit user feedback for a RAG response (Module B)
   */
  async submitFeedback(workspaceId, payload) {
    const res = await axiosInstance.post(
      '/knowledge/rag/feedback',
      payload,
      withWorkspace(workspaceId)
    );
    return res.data?.data;
  },

  /**
   * List RAG feedback items within workspace (Module B3)
   */
  async listFeedback(workspaceId, params = {}) {
    const config = {
      ...withWorkspace(workspaceId),
      params: {
        rating: params.rating && params.rating !== 'all' ? params.rating : undefined,
        reason: params.reason && params.reason !== 'all' ? params.reason : undefined,
        status: params.status && params.status !== 'all' ? params.status : undefined,
        sourceId: params.sourceId && params.sourceId !== 'all' ? params.sourceId : undefined,
      },
    };
    const res = await axiosInstance.get('/knowledge/rag/feedback', config);
    return res.data?.data || [];
  },

  /**
   * Update feedback status, record resolution/corrective action, or promote to eval case
   */
  async resolveFeedback(workspaceId, id, payload) {
    const res = await axiosInstance.patch(
      `/knowledge/rag/feedback/${id}`,
      payload,
      withWorkspace(workspaceId)
    );
    return res.data?.data;
  },

  /**
   * RAG Quality Evaluation & Continuous Quality Loop (Modules C & D)
   */
  async getEvalCases(workspaceId, params = {}) {
    const config = {
      ...withWorkspace(workspaceId),
      params,
    };
    const res = await axiosInstance.get('/knowledge/eval/cases', config);
    return res.data?.data || [];
  },

  async createEvalCase(workspaceId, payload) {
    const res = await axiosInstance.post(
      '/knowledge/eval/cases',
      payload,
      withWorkspace(workspaceId)
    );
    return res.data?.data;
  },

  async updateEvalCase(workspaceId, id, payload) {
    const res = await axiosInstance.put(
      `/knowledge/eval/cases/${id}`,
      payload,
      withWorkspace(workspaceId)
    );
    return res.data?.data;
  },

  async deleteEvalCase(workspaceId, id) {
    const res = await axiosInstance.delete(
      `/knowledge/eval/cases/${id}`,
      withWorkspace(workspaceId)
    );
    return res.data?.data || res.data;
  },

  async runEvaluation(workspaceId, payload = {}) {
    const res = await axiosInstance.post(
      '/knowledge/eval/run',
      payload,
      withWorkspace(workspaceId)
    );
    return res.data?.data;
  },

  async listEvalRuns(workspaceId, params = {}) {
    const config = {
      ...withWorkspace(workspaceId),
      params,
    };
    const res = await axiosInstance.get('/knowledge/eval/runs', config);
    return res.data?.data || [];
  },

  async getQualityDashboard(workspaceId) {
    const res = await axiosInstance.get('/knowledge/quality/dashboard', withWorkspace(workspaceId));
    return res.data?.data;
  },

  /**
   * Download RAG Evaluation Run Report in PDF format
   */
  async downloadEvaluationReport(workspaceId, runId = 'latest', filename, workspaceName) {
    const params = {};
    if (workspaceName) {
      params.workspaceName = workspaceName;
    }
    const res = await axiosInstance.get(`/knowledge/eval/runs/${runId}/pdf`, {
      ...withWorkspace(workspaceId),
      params,
      responseType: 'blob',
    });
    const runRef = (runId === 'latest' ? 'latest' : String(runId).slice(0, 8)).toUpperCase();
    const effectiveFilename = filename || `AI_LeadGen_RAG_Evaluation_Report_${runRef}.pdf`;
    const blob = new Blob([res.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = effectiveFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    return true;
  },

  /**
   * Get authorized pre-signed URL or details to view/download original document
   */
  async getSourceFile(workspaceId, id) {
    const res = await axiosInstance.get(
      `/knowledge/sources/${id}/file`,
      withWorkspace(workspaceId)
    );
    return res.data?.data;
  },
};

/**
 * Helper to safely extract API error message
 */
export function getKnowledgeErrorMessage(
  error,
  fallback = 'An unexpected error occurred while communicating with the knowledge service.'
) {
  return error?.response?.data?.error || error?.message || fallback;
}

export default knowledgeApi;
